/**
 * Prépare les données d'un sondage de dates pour l'export (CSV/JSON/MD/PDF).
 *
 * Le stockage Supabase garde `selections` keyé par `option.id` (ex. "1", "2", "3"),
 * mais `DatePollExportService` attend des clés par **date-string** (ex. "2026-04-20").
 * Cette fonction fait la conversion id -> date_string avant l'export.
 */

export interface DatePollExportOption {
  id: string | number;
  option_date: string;
}

export interface DatePollExportVote {
  id: string;
  voter_name?: string;
  voter_email?: string;
  selections?: Record<string, string>; // keyed by option.id (Supabase shape)
  created_at: string;
}

export interface DatePollExportPoll {
  id: string;
  title: string;
  description?: string;
  created_at: string;
}

export interface PreparedDatePollExportData {
  poll: {
    id: string;
    title: string;
    description?: string;
    created_at: string;
    dates: string[];
  };
  votes: Array<{
    id: string;
    voter_name: string;
    voter_email?: string;
    selections: Record<string, string>; // keyed by option_date
    created_at: string;
  }>;
}

export function prepareDatePollExportData(
  poll: DatePollExportPoll,
  options: DatePollExportOption[],
  votes: DatePollExportVote[],
): PreparedDatePollExportData {
  const idToDate = new Map<string, string>(
    options.map((opt) => [String(opt.id), opt.option_date]),
  );

  return {
    poll: {
      id: poll.id,
      title: poll.title,
      description: poll.description,
      created_at: poll.created_at,
      // Issue #759 — tri chronologique pour CSV/JSON/MD/PDF, indépendant de
      // l'ordre de clic à la création.
      dates: options.map((opt) => opt.option_date).sort(),
    },
    votes: votes.map((vote) => {
      const selectionsByDate: Record<string, string> = {};
      for (const [optionId, voteValue] of Object.entries(
        vote.selections || {},
      )) {
        const date = idToDate.get(String(optionId));
        if (date) selectionsByDate[date] = voteValue as string;
      }
      return {
        id: vote.id,
        voter_name: vote.voter_name || "Anonyme",
        voter_email: vote.voter_email,
        selections: selectionsByDate,
        created_at: vote.created_at,
      };
    }),
  };
}
